/**
 * DateRangePickerEditor
 *
 * Property editor for date-range-picker control configuration
 * Uses COMPOSITE PATTERN: start date + end date + presets
 *
 * Features:
 * - Start and end date defaults
 * - Preset date ranges (last 7 days, last 30 days, etc.)
 * - Date format configuration
 * - Range validation
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface DatePreset {
  label: string;
  rangeId: string;
  daysFromNow: number;
}

interface DateRangeConfig {
  defaultStartDate?: string;
  defaultEndDate?: string;
  dateFormat?: string;
  presets?: DatePreset[];
  minDate?: string;
  maxDate?: string;
  disabledDates?: string[];
}

function renderDateRangePickerEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as DateRangeConfig;

  const defaultPresets: DatePreset[] = [
    { label: 'Last 7 Days', rangeId: 'last-7-days', daysFromNow: -7 },
    { label: 'Last 30 Days', rangeId: 'last-30-days', daysFromNow: -30 },
    { label: 'Last 90 Days', rangeId: 'last-90-days', daysFromNow: -90 },
    { label: 'This Month', rangeId: 'this-month', daysFromNow: 0 },
    { label: 'Last Month', rangeId: 'last-month', daysFromNow: -30 },
  ];

  const presets = config.presets || defaultPresets;
  const dateFormat = config.dateFormat || 'YYYY-MM-DD';

  const handleStartDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('defaultStartDate', e.target.value);
    },
    [onConfigChange],
  );

  const handleEndDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('defaultEndDate', e.target.value);
    },
    [onConfigChange],
  );

  const handleAddPreset = useCallback(() => {
    const newPresets = [
      ...presets,
      { label: `Preset ${presets.length + 1}`, rangeId: `preset-${presets.length + 1}`, daysFromNow: -7 },
    ];
    onConfigChange('presets', newPresets);
  }, [presets, onConfigChange]);

  const handleUpdatePreset = useCallback(
    (index: number, field: keyof DatePreset, value: string | number) => {
      const updated = presets.map((p, i) =>
        i === index ? { ...p, [field]: value } : p,
      );
      onConfigChange('presets', updated);
    },
    [presets, onConfigChange],
  );

  const handleRemovePreset = useCallback(
    (index: number) => {
      onConfigChange('presets', presets.filter((_, i) => i !== index));
    },
    [presets, onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Date Range Picker Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="start-date-input" className="af-properties-tab__label">
          Default Start Date
        </label>
        <input
          id="start-date-input"
          type="date"
          value={config.defaultStartDate || ''}
          onChange={handleStartDateChange}
          className="af-properties-tab__input"
          aria-label="Default start date"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="end-date-input" className="af-properties-tab__label">
          Default End Date
        </label>
        <input
          id="end-date-input"
          type="date"
          value={config.defaultEndDate || ''}
          onChange={handleEndDateChange}
          className="af-properties-tab__input"
          aria-label="Default end date"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="date-format-select" className="af-properties-tab__label">
          Date Format
        </label>
        <select
          id="date-format-select"
          value={dateFormat}
          onChange={(e) => onConfigChange('dateFormat', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="YYYY-MM-DDTHH:mm">ISO DateTime</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Date Range Presets
        </label>
        {presets.map((preset, i) => (
          <div key={i} className="af-properties-tab__preset-row">
            <input
              type="text"
              value={preset.label}
              onChange={(e) => handleUpdatePreset(i, 'label', e.target.value)}
              className="af-properties-tab__input af-properties-tab__preset-label"
              placeholder="Preset label"
              aria-label={`Preset ${i + 1} label`}
            />
            <input
              type="text"
              value={preset.rangeId}
              onChange={(e) => handleUpdatePreset(i, 'rangeId', e.target.value)}
              className="af-properties-tab__input af-properties-tab__preset-id"
              placeholder="range-id"
              aria-label={`Preset ${i + 1} ID`}
            />
            <input
              type="number"
              value={preset.daysFromNow}
              onChange={(e) => handleUpdatePreset(i, 'daysFromNow', Number(e.target.value))}
              className="af-properties-tab__input af-properties-tab__preset-days"
              placeholder="days"
              aria-label={`Preset ${i + 1} days`}
            />
            <button
              onClick={() => handleRemovePreset(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddPreset}
          className="af-properties-tab__button af-properties-tab__button--primary"
          type="button"
        >
          Add Preset
        </button>
      </div>
    </div>
  );
}

export const DateRangePickerEditor: PropertyEditor = {
  type: 'date-range-picker',
  label: 'Date Range Picker',
  description: 'Configure date range picker with presets and formatting',
  render: renderDateRangePickerEditor,
};
